import { useAppSelector } from '../../../../redux/hooks.ts'
import {
  selectTheme,
  selectThemeStyle,
  toggleTheme,
} from '../../../../features/setting/setting.slice.ts'
import Segmented from 'antd/es/segmented'
import Select from 'antd/es/select'
import { useDispatch } from 'react-redux'
import LightModeOutlined from '@assets/icons/navigation/light-mode-outlined.svg'
import DarkModeOutlined from '@assets/icons/navigation/dark-mode-outlined.svg'
import DarkLightModeOutlined from '@assets/icons/navigation/dark-mode-light-mode-outlined.svg'
import DarkDarkModeOutlined from '@assets/icons/navigation/dark-mode-dark-mode-outlined.svg'
import {
  CalendarOutlined,
  LogoutOutlined,
  MessageOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
  TrophyOutlined,
  UserOutlined,
} from '@ant-design/icons'
import IdeyaTypography from '../../../antd/ideya-typography.tsx'
import React, { JSX, useState } from 'react'
import { IdeyaButton } from '../../../antd/ideya-button.tsx'
import { useNavigate } from 'react-router-dom'
import Divider from 'antd/es/divider'
import { Theme } from '../../../../features/setting/setting.def.ts'
import { useTranslation } from 'react-i18next'
import { selectUser } from '../../../../features/user/user.slice.ts'
import { useIsMobile } from '../../../hooks/use-is-mobile.ts'

export const UserMenu = () => {
  const themeStyle = useAppSelector(selectThemeStyle)
  const theme = useAppSelector(selectTheme)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en')
  const { t, i18n } = useTranslation()
  const userInfo = useAppSelector(selectUser)
  const isMobile = useIsMobile(768)

  const toggleThemeSwitch = () => {
    dispatch(toggleTheme())
  }

  const changeLanguage = (language: string) => {
    i18n.changeLanguage(language)
  }

  return (
    <div
      style={{
        borderRadius: themeStyle.borderRadiusLG,
        backgroundColor: themeStyle.contentBg,
        width: '100%',
        minWidth: 200,
        maxWidth: 400,
        boxShadow:
          '0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 9px 28px 8px rgba(0, 0, 0, 0.05)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: `${themeStyle.paddingBase} ${themeStyle.paddingContentHorizontalLG}`,
        gap: themeStyle.marginBase,
        touchAction: 'manipulation',
      }}
    >
      <Segmented
        onChange={toggleThemeSwitch}
        style={{ width: isMobile ? '78%' : '100%' }}
        options={[
          {
            value: 'Light',
            label: (
              <div
                style={{
                  display: 'flex',
                  flex: '1 0 0',
                  padding: '0px 12px',
                  gap: '4px',
                  alignSelf: 'stretch',
                  height: '28px',
                  width: '90px',
                  justifyContent: 'center',
                }}
              >
                <div style={{ margin: '6px 0px' }}>
                  {theme === Theme.Dark ? (
                    <DarkLightModeOutlined />
                  ) : (
                    <LightModeOutlined />
                  )}
                </div>
                <div>{t('user_menu.light')}</div>
              </div>
            ),
          },
          {
            value: 'Dark',
            label: (
              <div
                style={{
                  display: 'flex',
                  flex: '1 0 0',
                  padding: '0px 12px',
                  gap: '4px',
                  alignSelf: 'stretch',
                  height: '28px',
                  width: '90px',
                  justifyContent: 'center',
                }}
              >
                <div style={{ margin: '6px 0px' }}>
                  {theme === Theme.Dark ? (
                    <DarkDarkModeOutlined />
                  ) : (
                    <DarkModeOutlined />
                  )}
                </div>
                <div>{t('user_menu.dark')}</div>
              </div>
            ),
          },
        ]}
      />
      <div>
        <IdeyaTypography.Text type={'secondary'} size={'sm'}>
          {t('user_menu.user')}
        </IdeyaTypography.Text>
        <Divider />
      </div>
      <div>
        <MenuNavigationButton
          icon={<UserOutlined />}
          label={t('user_menu.profile')}
        />
      </div>
      <div>
        <MenuNavigationButton
          icon={<TrophyOutlined />}
          label={t('user_menu.my_achievements')}
        />
      </div>
      <div>
        <IdeyaTypography.Text type={'secondary'} size={'sm'}>
          {t('user_menu.system')}
        </IdeyaTypography.Text>
        <Divider />
      </div>
      {userInfo.region !== 'Global' && (
        <div>
          <MenuNavigationButton
            icon={<ReadOutlined />}
            label={t('user_menu.rules')}
            onClick={() => navigate('/rules')}
          />
        </div>
      )}
      <div>
        <MenuNavigationButton
          icon={<QuestionCircleOutlined />}
          label={'FAQ'}
          onClick={() =>
            window.open(
              `https://asset.polymerideya.com/faq/${selectedLanguage}`,
              '_blank'
            )
          }
        />
      </div>
      <div>
        <MenuNavigationButton
          icon={<MessageOutlined />}
          label={t('user_menu.support')}
          onClick={() =>
            window.open('mailto:support@polymerideya.com', '_self')
          }
        />
      </div>
      <Select
        defaultValue={'en'}
        style={{ width: '100%' }}
        onChange={(value) => {
          setSelectedLanguage(value)
          changeLanguage(value)
        }}
        options={[
          { value: 'en', label: 'English' },
          { value: 'jp', label: '日本語' },
          { value: 'cn', label: '中文 (简体)' },
          { value: 'zh', label: '中文 (繁體)' },
        ]}
      />
      <Divider />
      <IdeyaButton icon={<CalendarOutlined />}>
        {t('user_menu.leave_dates_apply')}
      </IdeyaButton>
      <IdeyaButton
        icon={<LogoutOutlined />}
        style={{ backgroundColor: themeStyle.btnBgRound }}
        onClick={() => navigate('/logout')}
      >
        {t('user_menu.logout')}
      </IdeyaButton>
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          color: themeStyle.footerColorText,
          fontSize: 12,
          zIndex: 1,
        }}
      >
        Copyright © {new Date().getFullYear()} Polymer. <br />
        All rights reserved.
      </div>
    </div>
  )
}

interface MenuNavigationButtonProps {
  icon: JSX.Element
  label: string
  onClick?: () => void
}

const MenuNavigationButton: React.FC<MenuNavigationButtonProps> = ({
  icon,
  label,
  onClick,
}) => {
  const themeStyle = useAppSelector(selectThemeStyle)

  return (
    <div
      onClick={onClick}
      style={{
        cursor: onClick ? 'pointer' : undefined,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {React.cloneElement(icon, {
        style: {
          color: themeStyle.naviDropdownIcon,
          fontSize: '20px',
          marginRight: themeStyle.marginXS,
        },
      })}
      <IdeyaTypography.Text size={'lg'}>{label}</IdeyaTypography.Text>
    </div>
  )
}
